import { clientHostOf, returnAddressKind, type ReturnAddressKind } from "@omnabase/shared";

/** M9 round two: the pure reading of the consent page's address (brief task 2, B2). Better Auth sends the member to `/oauth/consent` (or to
 *  `/signin` when signed out) with a signed query: the authorisation's parameters, `ba_param` naming every signed one, and `sig`. The page
 *  posts exactly the signed parameters back as `oauth_query`; anything the app adds to the address (the sign-in screen's `step` and `email`)
 *  is left out, or the signature would not hold. */

/** the signed part of an address's query, or null when it carries none */
export function signedQueryOf(search: string): string | null {
  const params = new URLSearchParams(search);
  const names = new Set(params.getAll("ba_param"));
  if (!params.get("sig") || !names.size) return null;
  const signed = new URLSearchParams();
  for (const [k, v] of params.entries()) if (k === "sig" || k === "ba_param" || names.has(k)) signed.append(k, v);
  return signed.toString();
}

export type ConsentRequest = {
  /** the workspace from the signed connector address (`…/mcp/<slug>`); null when it names none or several */
  slug: string | null;
  /** the host of a URL-shaped client id (Claude's published document: claude.ai); null for a registration's random id */
  clientHost: string | null;
  /** the host the sign-in goes back to (claude.ai, or 127.0.0.1 / localhost for Claude Code) */
  returnHost: string | null;
  /** what the return address is by shared's one rule (the server's): an unknown https app gets the warning and the checkbox, and the
   *  page posts the member's confirmation as `confirmUnknown` */
  returnKind: ReturnAddressKind;
  /** asks to stay connected (a refresh token) */
  offline: boolean;
};

/** what the page shows, read from the signed query alone (the server checks everything again on the POST) */
export function consentRequestOf(query: string): ConsentRequest {
  const p = new URLSearchParams(query);
  const resources = p.getAll("resource");
  const slug = resources.length === 1 ? /\/mcp\/([a-z0-9-]+)$/.exec(safePath(resources[0]!))?.[1] ?? null : null;
  const redirect = p.get("redirect_uri") ?? "";
  let returnHost: string | null = null;
  try { returnHost = new URL(redirect).hostname || null; } catch { returnHost = null; }
  return { slug, clientHost: clientHostOf(p.get("client_id")), returnHost, returnKind: returnAddressKind(redirect), offline: (p.get("scope") ?? "").split(" ").includes("offline_access") };
}

/** where the consent page posts its answer (the library's route, same origin) */
export const CONSENT_ROUTE = "/api/auth/oauth2/consent";
/** the consent POST's body: the member's answer and the signed query, with the confirmation for an unknown app (only ever `true` when the
 *  member ticked the box; the server refuses an Allow for an unknown app without it) */
export function consentBody(o: { accept: boolean; query: string; confirmUnknown: boolean }): { accept: boolean; oauth_query: string; confirmUnknown?: true } {
  return { accept: o.accept, oauth_query: o.query, ...(o.accept && o.confirmUnknown ? { confirmUnknown: true as const } : {}) };
}
const safePath = (url: string) => { try { return new URL(url).pathname; } catch { return ""; } };
